<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useToast } from "vue-toast-notification";
import { Minus, Plus, RefreshCw } from "lucide-vue-next";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import type { NormalizedClient } from "@/composables/useClientsQuery";
import { updateClient, type ClientAccountStatus } from "@/services/clientService";
import { useAuthStore } from "@/store/auth";
import RenewalDateInput from "@/components/dashboard/renewals/RenewalDateInput.vue";
import {
  addCalendarMonths,
  calculateRenewalSchedule,
  inferRenewalTermMonths,
} from "@/utils/renewalSchedule";

const MIN_RENEWAL_TERM_MONTHS = 1;

const props = defineProps<{
  open: boolean;
  client: NormalizedClient | null;
  renewalNote?: string | null;
  accountStatus?: string;
  accountStatusOptions: readonly ClientAccountStatus[];
}>();

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
}>();

const authStore = useAuthStore();
const queryClient = useQueryClient();
const toast = useToast();
const isSaving = ref(false);
const formError = ref("");
const renewalTermMonths = ref(3);
const renewalStartWasEdited = ref(false);
const initialAccountStatus = ref("");
const hasLegacyAccountStatus = computed(() => !props.accountStatusOptions.includes(
  initialAccountStatus.value as ClientAccountStatus
));

const form = reactive({
  onboarding_date: "",
  subscription_start_date: "",
  renewal_date: "",
  payment_due_date: "",
  renewal_start_date: "",
  renewal_end_date: "",
  client_account_status: "",
  renewal_note: "",
});

function normalizeDateInput(value: unknown) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) return rawValue.slice(0, 10);

  const parsed = new Date(rawValue);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}

function renewalValue(client: NormalizedClient, field: string) {
  return client.raw?.[field] || client.raw?.renewal_summary?.[field] || "";
}

function applyCalculatedSchedule() {
  const schedule = calculateRenewalSchedule(
    form.subscription_start_date,
    renewalTermMonths.value
  );
  if (!schedule.renewalDate) return;

  // Keep a manually chosen renewal start as the anchor. Opening the form never
  // generates dates, and unrelated controls do not create a missing window.
  if (form.renewal_start_date && !renewalStartWasEdited.value) {
    form.renewal_start_date = schedule.renewalStartDate;
  }
  if (form.renewal_end_date) {
    form.renewal_end_date = renewalStartWasEdited.value
      ? addCalendarMonths(form.renewal_start_date, renewalTermMonths.value)
      : schedule.renewalEndDate;
  }
  form.renewal_date = schedule.renewalDate;
}

function handleSubscriptionStartChange() {
  applyCalculatedSchedule();
}

function handleRenewalStartChange() {
  renewalStartWasEdited.value = true;
  form.renewal_end_date = addCalendarMonths(
    form.renewal_start_date,
    renewalTermMonths.value
  );
}

function adjustMonths(months: number) {
  if (!form.subscription_start_date && !form.renewal_start_date) {
    formError.value = "Set a subscription start or renewal start date before adjusting the renewal term.";
    return;
  }

  const nextTermMonths = renewalTermMonths.value + months;
  if (nextTermMonths < MIN_RENEWAL_TERM_MONTHS) return;

  renewalTermMonths.value = nextTermMonths;
  formError.value = "";
  if (renewalStartWasEdited.value || (form.renewal_start_date && form.renewal_end_date)) {
    handleRenewalStartChange();
  }
  else applyCalculatedSchedule();
}

function addMonths() {
  adjustMonths(1);
}

function subtractMonths() {
  adjustMonths(-1);
}

function hydrateForm(client: NormalizedClient | null, renewalNote?: string | null) {
  if (!client) return;
  renewalStartWasEdited.value = false;

  form.onboarding_date = normalizeDateInput(
    renewalValue(client, "onboarding_date") || client.onboardingDateValue
  );
  form.subscription_start_date = normalizeDateInput(
    renewalValue(client, "subscription_start_date") ||
      client.subscriptionStartDateValue
  );
  form.renewal_date = normalizeDateInput(
    renewalValue(client, "renewal_date") || client.renewalDateValue
  );
  form.payment_due_date = normalizeDateInput(
    renewalValue(client, "payment_due_date") || client.paymentDueDateValue
  );
  form.renewal_start_date = normalizeDateInput(
    renewalValue(client, "renewal_start_date") || client.renewalStartDateValue
  );
  form.renewal_end_date = normalizeDateInput(
    renewalValue(client, "renewal_end_date") || client.renewalEndDateValue
  );
  initialAccountStatus.value = String(
    props.accountStatus ?? client.raw?.client_account_status ?? client.clientAccountStatus ?? "Active"
  ).trim() || "Active";
  form.client_account_status = initialAccountStatus.value;
  form.renewal_note = String(
    renewalNote ?? client.raw?.renewal_note ?? ""
  ).trim();
  renewalTermMonths.value = inferRenewalTermMonths(
    form.renewal_start_date && form.renewal_end_date
      ? form.renewal_start_date : form.subscription_start_date,
    form.renewal_start_date && form.renewal_end_date
      ? form.renewal_end_date : form.renewal_date || form.renewal_end_date,
    3
  );
  formError.value = "";
}

watch(
  [() => props.client, () => props.open, () => props.renewalNote, () => props.accountStatus],
  ([client, open, renewalNote]) => {
    if (client && open) {
      hydrateForm(client, renewalNote);
    }
  },
  { immediate: true }
);

function validateForm() {
  if (form.client_account_status !== initialAccountStatus.value
    && !props.accountStatusOptions.includes(form.client_account_status as ClientAccountStatus)) {
    formError.value = "Select a valid account status.";
    return false;
  }
  if (form.renewal_note.length > 1000) {
    formError.value = "Renewal note must be 1,000 characters or fewer.";
    return false;
  }

  if (
    form.renewal_start_date &&
    form.renewal_end_date &&
    form.renewal_end_date < form.renewal_start_date
  ) {
    formError.value = "Renewal end must be on or after renewal start.";
    return false;
  }

  formError.value = "";
  return true;
}

function getErrorMessage(error: unknown) {
  const apiError = error as {
    response?: { data?: { message?: string } };
  };
  return (
    apiError.response?.data?.message ||
    "Renewal details could not be saved right now."
  );
}

async function saveRenewal() {
  if (isSaving.value || !props.client || !authStore.canEditRenewalTracking || !validateForm()) {
    return;
  }

  isSaving.value = true;

  try {
    await updateClient(props.client.raw?.id || props.client.id, {
      onboarding_date: form.onboarding_date || null,
      subscription_start_date: form.subscription_start_date || null,
      renewal_date: form.renewal_date || null,
      payment_due_date: form.payment_due_date || null,
      renewal_start_date: form.renewal_start_date || null,
      renewal_end_date: form.renewal_end_date || null,
      ...(form.client_account_status !== initialAccountStatus.value ? {
        client_account_status: form.client_account_status as ClientAccountStatus,
      } : {}),
      renewal_note: form.renewal_note.trim() || null,
    });

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["clients"] }),
      queryClient.invalidateQueries({ queryKey: ["renewal-tracking-client"] }),
    ]);

    toast.success("Renewal details updated.");
    emit("update:open", false);
  } catch (error) {
    toast.error(getErrorMessage(error));
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent
      class="max-w-3xl max-h-[90vh] rounded-[1.5rem] border-0 bg-white p-0 shadow-[0_32px_80px_rgba(23,32,51,0.2)] overflow-hidden"
    >
      <form
        class="flex max-h-[90vh] flex-col"
        @submit.prevent="saveRenewal"
      >
        <DialogHeader
          class="shrink-0 border-b border-[rgba(224,230,240,1)] bg-gradient-to-br from-[#f7fbff] via-white to-[#eef6ff] px-7 py-6"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="text-[0.68rem] font-extrabold uppercase tracking-[0.16em] text-blue-600">
                Renewal workspace
              </p>
              <DialogTitle class="mt-1 text-[1.45rem] font-extrabold tracking-[-0.02em] text-[#172033]">
                Edit Renewal
              </DialogTitle>
              <p class="mt-1 text-sm font-medium text-[#64748b]">
                {{ client?.companyName || "Account" }}
              </p>
            </div>
            <div class="hidden items-center gap-2 rounded-full border border-emerald-200 bg-white/80 px-3 py-1.5 text-xs font-bold text-emerald-700 sm:flex">
              <span class="h-2 w-2 rounded-full bg-emerald-500" />
              Live record
            </div>
          </div>
        </DialogHeader>

        <div class="min-h-0 overflow-y-auto bg-[#fbfdff] px-7 py-6">
          <section class="rounded-[1.25rem] border border-[rgba(219,227,239,0.95)] bg-white shadow-[0_10px_30px_rgba(57,84,120,0.06)]">
            <div class="border-b border-[rgba(230,236,245,1)] px-5 py-5">
              <div class="flex items-start gap-3">
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.85rem] bg-blue-100 text-lg text-blue-700">
                  <RefreshCw class="h-5 w-5" />
                </div>
                <div>
                  <p class="text-[1rem] font-extrabold text-[#172033]">
                    Renewal Tracking
                  </p>
                  <p class="mt-1 max-w-xl text-[0.8rem] leading-5 text-[#64748b]">
                    Keep subscription dates, payment timing, and renewal notes aligned for this account.
                  </p>
                </div>
              </div>
            </div>

            <div class="mx-5 my-5 flex items-center justify-between gap-3 rounded-[1rem] border border-blue-100 bg-gradient-to-r from-[#eff6ff] to-[#f7fbff] p-4">
              <div>
                <p class="text-sm font-extrabold text-[#172033]">
                  <span data-testid="renewal-term-label">
                    {{ renewalTermMonths }} month{{ renewalTermMonths === 1 ? "" : "s" }}
                  </span>
                </p>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  :disabled="renewalTermMonths <= MIN_RENEWAL_TERM_MONTHS"
                  class="h-10 w-10 shrink-0 border-blue-200 bg-white text-blue-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Remove one month from renewal term"
                  data-testid="renewal-subtract-month"
                  @click="subtractMonths"
                >
                  <Minus class="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  class="h-10 w-10 shrink-0 border-blue-200 bg-white text-blue-700 shadow-sm hover:border-blue-300 hover:bg-blue-50"
                  aria-label="Add one month to renewal term"
                  data-testid="renewal-add-month"
                  @click="addMonths"
                >
                  <Plus class="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div class="grid gap-x-5 gap-y-4 px-5 pb-5 sm:grid-cols-2">
              <div class="sm:col-span-2">
                <p class="text-[0.68rem] font-extrabold uppercase tracking-[0.13em] text-[#8a97aa]">
                  Account timeline
                </p>
              </div>
              <label class="flex flex-col gap-[0.42rem]">
                <span class="text-[0.8rem] font-bold text-[#64748b]">
                  Onboarding date
                </span>
                <RenewalDateInput
                  v-model="form.onboarding_date"
                  type="date"
                  data-testid="renewal-onboarding-date"
                  class="min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                />
              </label>
              <label class="flex flex-col gap-[0.42rem]">
                <span class="text-[0.8rem] font-bold text-[#64748b]">
                  Subscription start
                </span>
                <RenewalDateInput
                  v-model="form.subscription_start_date"
                  type="date"
                  data-testid="renewal-subscription-start"
                  class="min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                  @change="handleSubscriptionStartChange"
                />
              </label>
              <div class="sm:col-span-2 mt-1 flex items-center gap-3 border-t border-[rgba(230,236,245,1)] pt-4">
                <p class="text-[0.68rem] font-extrabold uppercase tracking-[0.13em] text-[#8a97aa]">
                  Renewal schedule
                </p>
                <span class="h-px flex-1 bg-[rgba(230,236,245,1)]" />
              </div>
              <div class="flex flex-col gap-[0.42rem]">
                <span class="text-[0.8rem] font-bold text-[#64748b]">Renewal date</span>
                <RenewalDateInput
                  v-model="form.renewal_date"
                  type="date"
                  data-testid="renewal-date"
                  class="min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                />
              </div>

              <label class="flex flex-col gap-[0.42rem]">
                <span class="text-[0.8rem] font-bold text-[#64748b]">Payment due date</span>
                <RenewalDateInput
                  v-model="form.payment_due_date"
                  type="date"
                  data-testid="renewal-payment-due-date"
                  class="min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                />
              </label>

              <label class="flex flex-col gap-[0.42rem]">
                <span class="text-[0.8rem] font-bold text-[#64748b]">Renewal start</span>
                <RenewalDateInput
                  v-model="form.renewal_start_date"
                  type="date"
                  data-testid="renewal-start-date"
                  class="min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                  @change="handleRenewalStartChange"
                />
              </label>

              <label class="flex flex-col gap-[0.42rem]">
                <span class="text-[0.8rem] font-bold text-[#64748b]">Renewal end</span>
                <RenewalDateInput
                  v-model="form.renewal_end_date"
                  type="date"
                  data-testid="renewal-end-date"
                  class="min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                />
              </label>

              <div class="sm:col-span-2 mt-1 flex items-center gap-3 border-t border-[rgba(230,236,245,1)] pt-4">
                <p class="text-[0.68rem] font-extrabold uppercase tracking-[0.13em] text-[#8a97aa]">
                  Account status and notes
                </p>
                <span class="h-px flex-1 bg-[rgba(230,236,245,1)]" />
              </div>

              <label class="flex flex-col gap-[0.42rem]">
                <span class="text-[0.8rem] font-bold text-[#64748b]">Account status</span>
                <select
                  v-model="form.client_account_status"
                  aria-label="Account status"
                  class="min-h-[2.75rem] rounded-[0.95rem] border border-[rgba(211,220,233,1)] bg-white px-3 text-sm font-semibold text-[#243049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#8eb4ff]"
                >
                  <option
                    v-if="hasLegacyAccountStatus"
                    :value="initialAccountStatus"
                    disabled
                  >
                    {{ initialAccountStatus }} (current)
                  </option>
                  <option
                    v-for="status in accountStatusOptions"
                    :key="status"
                    :value="status"
                  >
                    {{ status }}
                  </option>
                </select>
              </label>

              <label class="flex flex-col gap-[0.42rem] sm:col-span-2">
                <span class="text-[0.8rem] font-bold text-[#64748b]">Renewal note</span>
                <Textarea
                  v-model="form.renewal_note"
                  rows="4"
                  maxlength="1000"
                  placeholder="Record a payment confirmation, follow-up, or renewal note."
                  class="min-h-[6.5rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049] shadow-sm"
                />
              </label>
            </div>

            <p
              v-if="formError"
              class="mx-5 mb-5 rounded-[0.8rem] border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600"
            >
              {{ formError }}
            </p>
          </section>
        </div>

        <DialogFooter
          class="shrink-0 gap-2 border-t border-[rgba(224,230,240,1)] bg-white px-7 py-5"
        >
          <Button
            type="button"
            variant="outline"
            :disabled="isSaving"
            @click="emit('update:open', false)"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :disabled="isSaving || !authStore.canEditRenewalTracking"
            class="bg-blue-600 text-white hover:bg-blue-700"
          >
            {{ isSaving ? "Saving..." : "Save renewal" }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
